import React, {useContext, useEffect, useState} from "react";
import {Select, MenuItem, InputLabel, Button} from "@mui/material";
import DatePicker from "react-datepicker";
import {benefits, gamesAverage, notPlayed, participants, games, commitedGamesOfParticipants} from '@api/statistics'
import {LoadingContext} from "@hoc/loading";
import {DataGrid} from '@mui/x-data-grid';
import {renderCellExpand} from "@components/renderCellExpand";
import {MessageContext} from "@hoc/messageNotification";
import Box from "@mui/material/Box";
import {CSVLink} from "react-csv";
import Typography from "@mui/material/Typography";
import moment from "moment-timezone";
import Grid from "@mui/material/Grid/Grid";
import makeStyles from "@material-ui/core/styles/makeStyles";
import TimeZone from "../TimeZone";
import {getTimeZoneValue} from "../../api/timeZone";
import {renderForm} from "../Form/renderForm";
import {EMPTY_SEGMENT, TYPES} from "../../constants";

const useStyles = makeStyles({
    list:{
      paddingLeft: '20px',
      fontWeight: 'bold'
    },
    body: {
        display: 'flex',
        alignItems: 'center',
        textAlign: 'center',
    },
    hidden: {
        display: 'none'
    },
    downloadLink: {
        textAlign: 'center',
        margin: '0 auto',
        width: '50%',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
    },
});

export const Statistics = () => {
    const classes = useStyles();

    const columnsDataGrid = [
        {field: 'shopperId', headerName: 'shopperId', renderCell: renderCellExpand},
        {field: 'amountOfGamesPlayed',  minWidth: 200, headerName: 'amountOfGamesPlayed', renderCell: renderCellExpand},
    ];

    const onCallback = (e) => {
        columns.forEach((column) => {
            if (column.name === e.target.name) {
                column.value = e.target.value;
                column.error = '';
            }
        })
        setColumns([...columns]);
    }

    const columnsData = [
        {name: 'type', headerName: 'type', type: 'enum', value: '', items: ['ALL', ...TYPES], onCallback: onCallback},
        {name: 'segment', headerName: 'segment', value: [], error: '', empty: true, type: 'segment', onCallback},
        {name: 'category', headerName: 'category', value: [], error: '', type: 'category', onCallback},
    ];
    const [columns, setColumns] = useState(columnsData);

    const [rows, setRows] = useState(null);
    const [total, setTotal] = useState({});
    const {setLoading} = useContext(LoadingContext);
    const {setErrorMessage} = useContext(MessageContext);
    const [datePickerFrom, setDatePickerFrom] = useState(new Date());
    const [datePickerUntil, setDatePickerUntil] = useState(new Date());
    const [userRows, setUserRows] = useState()
    const [dataModel, setDataModel] = useState("benefits");
    const [assignReason, setAssignReason] = useState('FREE')
    const [listCsv, setListCsv] = useState()

    const req = {benefits, notPlayed, gamesAverage, participants, games, commitedGamesOfParticipants};

    const onDataModelChange = (dataModel) => {
        setDataModel(dataModel);
    };

    const formatData = (data) => {
        const obj = {label: '', value: ''};
        switch (dataModel) {
            case 'benefits':
                obj.label = 'totalCount';
                obj.value = data;
                setRows(data);
                break;
            case 'games':
                obj.label = 'Total all type of games played';
                obj.value = data;
                setRows(data);
                break;
            case 'gamesAverage':
                obj.label = 'totalCount';
                obj.value = data;
                setRows(data);
                break;
            case 'participants':
                obj.label = 'newRegistrationAmount';
                obj.value = [data];
                setRows(data);
                break;
            case 'notPlayed':
                obj.label = 'notPlayed';
                obj.value = [data];
                setRows(data);
                break;
            case 'commitedGamesOfParticipants':
                obj.label = 'commitedGamesOfParticipants';
                obj.value = data;
                setRows(data);
                break;
            default:
                obj.label = 'totalCount';
                obj.value = data.length;
                setRows(data);
        }

        setTotal(obj);
    }

    const onTimeRangeChange = (e) => {
        setAssignReason(e)
    }

    const submit = async () => {
        try {
            setRows(null);
            setUserRows(null)
            setLoading(true);
            setErrorMessage(null);
            // To calculate the time difference of two dates
            let timeDifferenceBetweenInputs = datePickerUntil.getTime() - datePickerFrom.getTime();

            let daysDifferenceBetweenInputs = timeDifferenceBetweenInputs / (1000 * 3600 * 24);
            let differenceInDaysMaxCheck = Math.round(daysDifferenceBetweenInputs);
            if (differenceInDaysMaxCheck > 7) {
                alert("You cannot see the statistics for the time period more than 7 days")
            } else {
                let formattedFrom = moment(datePickerFrom, 'MM/DD/YYYY').tz(getTimeZoneValue()).hour(0).minute(0).format();
                let formattedUntil = moment(datePickerUntil, 'MM/DD/YYYY').tz(getTimeZoneValue()).hour(0).minute(0).format();
                if (dataModel === 'participants' || dataModel === 'commitedGamesOfParticipants') {
                    const data = await req[dataModel]({from: formattedFrom, until: formattedUntil, assignReason})
                    formatData(data);
                }else if (['games', 'benefits'].includes(dataModel)) {
                    const obj = {
                        from: formattedFrom,
                        until: formattedUntil,
                        segments: columns.find((column) => column.name === 'segment').value,
                        categoryId: columns.find((column) => column.name === 'category').value.id
                    }

                    if(obj.segments.find((value) => value === EMPTY_SEGMENT.name)) {
                        obj.segments = []
                    }else if(!obj.segments.length) {
                        obj.segments = null
                    }

                    const type = columns.find((column) => column.name === 'type').value
                    if(type && TYPES.includes(type)) obj.type = type

                    const data = await req[dataModel]({...obj, assignReason})
                    formatData(data);
                } else {
                    const data = await req[dataModel]({
                        from: formattedFrom,
                        until: formattedUntil,
                        assignReason: assignReason
                    })
                    formatData(data);
                }
            }
        } catch (e) {
            setErrorMessage(e)
        }
        setLoading(false);
    }

    function buildTreeCsvFunc(value){
        if(!value) return ;

        let data = []
        let arr = []

        switch (dataModel) {
            case 'benefits':
                data.push(['purchasedDesc', 'totalBarcodesIssued', 'benefitName', 'benefitType', 'totalBarcodesIssued'])

                value.forEach(({purchasedDesc, totalBarcodesIssued: total, details}) => {
                    details.forEach(({benefitName, benefitType, totalBarcodesIssued}, ind) => {
                        if(ind === 0){
                            arr.push([purchasedDesc || 'No name', total, benefitName, benefitType, totalBarcodesIssued])
                        }else{
                            arr.push(['', '', benefitName, benefitType, totalBarcodesIssued])
                        }
                    })
                    return arr
                })

                data = data.concat(arr)
                break;
            case 'games':
                data.push(['purchasedDesc', 'totalCount', 'type', 'benefitsTotalCount'])

                value.forEach(({purchasedDesc, totalCount, benefits}) => {
                    benefits.forEach(({totalCount: benefitsTotalCount, type}, ind) => {
                        if(ind === 0){
                            arr.push([purchasedDesc || 'No name', totalCount, type, benefitsTotalCount])
                        }else{
                            arr.push(['', '', type, benefitsTotalCount])
                        }
                    })
                    return arr
                })

                data = data.concat(arr)
                break;
            case 'gamesAverage':
                data.push(['amount', 'playedPercentage', 'totalUsersPlayed'])
                data = data.concat(value.map(({amount, playedPercentage, totalUsersPlayed}) => { return [amount, playedPercentage, totalUsersPlayed]}))
                break;
            case 'participants':
                data.push(['newRegistrationAmount', 'uniqueParticipantsAmount'])
                data = data.concat(value.map(({newRegistrationAmount, uniqueParticipantsAmount}) => { return [newRegistrationAmount, uniqueParticipantsAmount]}))
                break;
            case 'commitedGamesOfParticipants':
                data.push(['amountOfGamesPlayed', 'shopperId'])
                data = data.concat(value.map(({amountOfGamesPlayed, shopperId}) => { return [amountOfGamesPlayed, shopperId]}))
                break;
            case 'notPlayed':
                data.push(['shopperId'])
                data = data.concat(value.map(({shopperId}) => { return [shopperId]}))
                break;
            default:
        }

        return data
    }

    function generateColumns(titles){
        return titles.map((title) => {
            return {field: title, headerName: title}
        })
    }

    function generateRow(data){
        if(data.length < 2) return []

        const titles = data[0]

        return data.slice(1, data.length).map((row, id) => {
            const obj = {id}
            titles.map((title, ind) => {
                obj[title] = row[ind]
            })

            return obj
        })
    }

    useEffect(() => {
        // setListArr(buildTree([], total.value))
        setListCsv(buildTreeCsvFunc(total.value))
    }, [total.value])

    return (
        <Grid container spacing={1}>
            <Grid item xs={6}>
                <Box display={'flex'} marginBottom={3}>
                    <Typography component="div" sx={{color: "red", display: "inline"}}>Note:</Typography>
                    <Typography component="div" sx={{display: "inline-block", width: 400}}>
                        Statistics requests are pretty heavy.
                        Please use short periods and try not to run requests frequently! Try to avoid running requests
                        during pick load times!
                    </Typography>
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Grid container spacing={1} className={classes.body}>
                    <Grid item xs={6}></Grid>
                    <Grid item xs={5}>
                        <TimeZone />
                    </Grid>
                    <Grid item xs={1}>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={1} className={classes.body}>
                    <Grid item xs={6} md={['games', 'benefits'].includes(dataModel) ? 2 : 4}>
                        <Select
                            value={dataModel}
                            label="Age"
                            onChange={(e) => onDataModelChange(e.target.value)}
                        >
                            <MenuItem value={"benefits"}>Benefits</MenuItem>
                            <MenuItem value={"games"}>Games</MenuItem>
                            <MenuItem value={"gamesAverage"}>Games played on average</MenuItem>
                            <MenuItem value={"participants"}>Participants</MenuItem>
                            <MenuItem value={"commitedGamesOfParticipants"}>Commited games</MenuItem>
                            <MenuItem value={"notPlayed"}>Not played</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={6} md={2} className={['games', 'benefits'].includes(dataModel) ? '' : classes.hidden}>
                        <Box  display={"flex"}
                              justifyContent={"center"}
                              alignItems={"center"}
                              component="form"
                              flexDirection={"column"}
                              style={{
                                  margin: '5px 0'
                              }}
                        >
                            {
                                columns.map((data, key) => {
                                    return renderForm(data, key);
                                })
                            }
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        {dataModel !== "participants" ? <Box>
                            <Select
                                value={assignReason}
                                label="Assign reason"
                                onChange={(e) => onTimeRangeChange(e.target.value)}
                            >
                                <MenuItem value={null}>All</MenuItem>
                                <MenuItem value={"FREE"}>Free</MenuItem>
                                <MenuItem value={"PURCHASED"}>Purchased</MenuItem>
                            </Select>
                        </Box> : ""}
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <InputLabel htmlFor="input-with-icon-adornment">From</InputLabel>
                        <DatePicker
                            selected={datePickerFrom}
                            name="datePickerFrom"
                            onChange={(e) => setDatePickerFrom(e)}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <InputLabel htmlFor="input-with-icon-adornment">Until</InputLabel>
                        <DatePicker
                            selected={datePickerUntil}
                            name="datePickerUntil"
                            onChange={(e) => setDatePickerUntil(e)}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button variant="contained" onClick={submit}>Show Statistics</Button>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                {rows ? (<Box padding={10} display={'flex'} alignItems={"center"} justifyContent={"space-around"}>
                    {typeof total.value === 'object' ?
                        <Box style={{width: '500px'}}>
                            {listCsv ? <DataGrid
                                    autoHeight={true}
                                    rows={generateRow(listCsv)}
                                    columns={generateColumns(listCsv[0])}
                                />
                                : ''}
                        </Box>
                        :
                        <Box>
                            {/*<Typography fontSize={22} component="h3">{total.label} - {typeof total.value === 'string' ? total.value : <pre>{JSON.stringify(total.value, null, 2)}</pre>}</Typography>*/}
                            {total.label2 ?
                                <Typography fontSize={22} component="h3">{total.label2} - {total.value2}</Typography> : ""}
                        </Box>
                    }
                    <Box display={'flex'}
                         flexDirection={'column'}
                         justifyContent={'center'}>
                        {listCsv?.length ? <>
                            <Typography fontSize={22} component="h3">Download detailed statistics</Typography>
                            <Button variant="contained"><CSVLink data={listCsv ? listCsv : []}>Download</CSVLink></Button>
                        </> : ''}
                    </Box>
                </Box>) : ''}
                {userRows ? <DataGrid
                        autoHeight={true}
                        rows={userRows}
                        columns={columnsDataGrid}
                    />
                    : ''}
                <Box className={classes.downloadLink}>
                    {userRows ?
                        <>
                            <Typography fontSize={22} component="h3">Download detailed statistics</Typography>
                            <Button variant="contained"><CSVLink data={userRows ? userRows : []}>Download</CSVLink></Button>
                        </> : ''}
                </Box>
            </Grid>
        </Grid>
    );
};
